import {useCallback, useEffect, useRef, useState} from "react";

import {useGeolocation} from "@pg-mono/geo-utils";
import {RegionType} from "@pg-rp/common-types";
import {PRICES_CITIES} from "@pg-rp/navigation";

import {fetchRegionsByCoordinates, IRegionByCoordinates} from "../api/fetch_regions_by_coordinates";
import {PRICES_SEARCH_GEOLOCATION_TIMEOUT_MS} from "../constants/prices_search";
import type {IPricesSearchNearbyState, IPricesSearchNearbyTarget} from "../types/IPricesSearchModel";
import {getCityLink} from "../utils/get_city_link";
import {getDistrictLink} from "../utils/prices_search/get_district_link";
import {isValidSearchInputRegion} from "../utils/prices_search/is_valid_search_input_region";

/**
 * Why the last attempt ended in `failed`. `geolocation` is a position the device could not get; a denied permission
 * is not a failure but the `blocked` state.
 */
export type PricesSearchNearbyFailureReason = "unsupported" | "geolocation" | "timeout" | "lookup" | "noRegion";

export interface IUsePricesSearchNearbyOptions {
    /** Called once per successful attempt, e.g. to save the recent entry and navigate. */
    onResolved?: (target: IPricesSearchNearbyTarget) => void;
}

export interface IUsePricesSearchNearbyResult {
    state: IPricesSearchNearbyState;
    /** Set only while `state.status === "failed"`. */
    failureReason: PricesSearchNearbyFailureReason | null;
    /** Asks the browser for the position - the permission prompt appears only now - and resolves the region. No-op while locating. */
    locate: () => void;
    /** Back to idle; a pending attempt is ignored from then on. */
    reset: () => void;
}

export interface IPricesSearchNearbyResolution {
    state: IPricesSearchNearbyState;
    failureReason: PricesSearchNearbyFailureReason | null;
}

const IDLE: IPricesSearchNearbyResolution = {state: {status: "idle"}, failureReason: null};
const BLOCKED: IPricesSearchNearbyResolution = {state: {status: "blocked"}, failureReason: null};

// GeolocationPositionError.PERMISSION_DENIED - the constant itself is missing where the API is.
const PERMISSION_DENIED_CODE = 1;

// Resolves null wherever the browser cannot say (no Permissions API, or "geolocation" not a name it knows).
const queryGeolocationPermission = async (): Promise<PermissionStatus | null> => {
    try {
        return (await navigator.permissions?.query({name: "geolocation"})) ?? null;
    } catch {
        return null;
    }
};

const PRICES_CITY_NAME_BY_SLUG = new Map<string, string>(PRICES_CITIES.map((city) => [city.slug, city.name]));

// A district of one of the 14 cities first, then the city itself; anywhere else the page does not exist.
export const resolvePricesSearchNearbyRegions = (regions: readonly IRegionByCoordinates[]): IPricesSearchNearbyResolution => {
    const district = regions.find((region) => region.type === RegionType.DISTRICT && region.parent && PRICES_CITY_NAME_BY_SLUG.has(region.parent.slug));

    if (district?.parent) {
        const citySlug = district.parent.slug;
        const target: IPricesSearchNearbyTarget = {
            kind: "district",
            title: district.name,
            href: getDistrictLink(citySlug, district),
            citySlug,
            regionId: district.id,
            // The coordinates lookup uses the region search scenario, so the object is the one the main search
            // stores - but only a region that passes the store's own validation goes there.
            ...(isValidSearchInputRegion(district) ? {region: district} : {})
        };

        return {state: {status: "resolved", target}, failureReason: null};
    }

    const city = regions.find((region) => region.type === RegionType.CITY && PRICES_CITY_NAME_BY_SLUG.has(region.slug));

    if (city) {
        const target: IPricesSearchNearbyTarget = {
            kind: "city",
            title: PRICES_CITY_NAME_BY_SLUG.get(city.slug) ?? city.name,
            href: getCityLink(city.slug),
            citySlug: city.slug,
            regionId: city.id
        };

        return {state: {status: "resolved", target}, failureReason: null};
    }

    const place = regions.find((region) => region.type === RegionType.CITY || region.type === RegionType.TOWN);

    if (place) {
        return {state: {status: "outsideCoverage", placeName: place.name}, failureReason: null};
    }

    // Outside Poland, or only a voivodeship or county came back - there is no place name worth showing.
    return {state: {status: "failed"}, failureReason: "noRegion"};
};

/**
 * "Ceny w mojej okolicy": the device position mapped to a district or city with a prices page. Nothing is
 * asked of the browser until `locate()`; the whole attempt (position and region lookup) gives up after
 * PRICES_SEARCH_GEOLOCATION_TIMEOUT_MS. A site blocked from location is `blocked` - read from the Permissions API
 * up front where the browser has it, otherwise from a denied `locate()` - and goes back to `idle` once the user
 * allows it in the site settings.
 */
export const usePricesSearchNearby = (options: IUsePricesSearchNearbyOptions = {}): IUsePricesSearchNearbyResult => {
    const {locateUser} = useGeolocation();
    const [result, setResult] = useState<IPricesSearchNearbyResolution>(IDLE);
    const isBlockedRef = useRef(false);

    const activeAttemptRef = useRef<number | null>(null);
    const attemptCounterRef = useRef(0);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The latest callbacks, so `locate` can stay stable across renders.
    const locateUserRef = useRef(locateUser);
    const onResolvedRef = useRef(options.onResolved);

    useEffect(() => {
        locateUserRef.current = locateUser;
        onResolvedRef.current = options.onResolved;
    });

    const clearAttempt = useCallback(() => {
        activeAttemptRef.current = null;

        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const settle = useCallback(
        (attempt: number, next: IPricesSearchNearbyResolution) => {
            if (activeAttemptRef.current !== attempt) {
                return;
            }

            clearAttempt();
            setResult(next);

            if (next.state.status === "resolved") {
                onResolvedRef.current?.(next.state.target);
            }
        },
        [clearAttempt]
    );

    const setBlocked = useCallback(
        (isBlocked: boolean) => {
            isBlockedRef.current = isBlocked;

            if (isBlocked) {
                // An attempt in flight has no chance any more.
                clearAttempt();
                setResult(BLOCKED);
            } else {
                setResult((previous) => (previous.state.status === "blocked" ? IDLE : previous));
            }
        },
        [clearAttempt]
    );

    useEffect(() => {
        let isMounted = true;
        let permission: PermissionStatus | null = null;
        const onPermissionChange = () => permission && setBlocked(permission.state === "denied");

        void queryGeolocationPermission().then((status) => {
            if (!isMounted || !status) {
                return;
            }

            permission = status;
            onPermissionChange();
            status.addEventListener("change", onPermissionChange);
        });

        return () => {
            isMounted = false;
            permission?.removeEventListener("change", onPermissionChange);
        };
    }, [setBlocked]);

    const locate = useCallback(() => {
        if (activeAttemptRef.current !== null || isBlockedRef.current) {
            return;
        }

        attemptCounterRef.current += 1;
        const attempt = attemptCounterRef.current;
        activeAttemptRef.current = attempt;

        // useGeolocation silently does nothing without the API, so this case is caught here.
        if (typeof navigator === "undefined" || !navigator.geolocation) {
            settle(attempt, {state: {status: "failed"}, failureReason: "unsupported"});
            return;
        }

        setResult({state: {status: "locating"}, failureReason: null});
        timeoutRef.current = setTimeout(() => settle(attempt, {state: {status: "failed"}, failureReason: "timeout"}), PRICES_SEARCH_GEOLOCATION_TIMEOUT_MS);

        locateUserRef.current(
            (coords) => {
                if (activeAttemptRef.current !== attempt) {
                    return;
                }

                // Two-argument then: an error thrown by `onResolved` must not be reported as a failed lookup.
                void fetchRegionsByCoordinates({}, coords.latitude, coords.longitude).then(
                    (regions) => settle(attempt, resolvePricesSearchNearbyRegions(regions)),
                    () => settle(attempt, {state: {status: "failed"}, failureReason: "lookup"})
                );
            },
            (error) => {
                if (activeAttemptRef.current !== attempt) {
                    return;
                }

                if (error?.code === PERMISSION_DENIED_CODE) {
                    setBlocked(true);
                    return;
                }

                settle(attempt, {state: {status: "failed"}, failureReason: "geolocation"});
            }
        );
    }, [settle, setBlocked]);

    const reset = useCallback(() => {
        clearAttempt();
        setResult(isBlockedRef.current ? BLOCKED : IDLE);
    }, [clearAttempt]);

    useEffect(() => clearAttempt, [clearAttempt]);

    return {state: result.state, failureReason: result.failureReason, locate, reset};
};
import {useCallback, useEffect, useRef, useState} from "react";

import {useGeolocation} from "@pg-mono/geo-utils";
import {RegionType} from "@pg-rp/common-types";
import {PRICES_CITIES} from "@pg-rp/navigation";

import {fetchRegionsByCoordinates, IRegionByCoordinates} from "../api/fetch_regions_by_coordinates";
import {PRICES_SEARCH_GEOLOCATION_TIMEOUT_MS} from "../constants/prices_search";
import type {IPricesSearchNearbyState, IPricesSearchNearbyTarget} from "../types/IPricesSearchModel";
import {getCityLink} from "../utils/get_city_link";
import {getDistrictLink} from "../utils/prices_search/get_district_link";
import {isValidSearchInputRegion} from "../utils/prices_search/is_valid_search_input_region";

/**
 * Why the last attempt ended in `failed`. `geolocation` is a position the device could not get; a denied permission
 * is not a failure but the `blocked` state.
 */
export type PricesSearchNearbyFailureReason = "unsupported" | "geolocation" | "timeout" | "lookup" | "noRegion";

export interface IUsePricesSearchNearbyOptions {
    /** Called once per successful attempt, e.g. to save the recent entry and navigate. */
    onResolved?: (target: IPricesSearchNearbyTarget) => void;
}

export interface IUsePricesSearchNearbyResult {
    state: IPricesSearchNearbyState;
    /** Set only while `state.status === "failed"`. */
    failureReason: PricesSearchNearbyFailureReason | null;
    /** Asks the browser for the position - the permission prompt appears only now - and resolves the region. No-op while locating. */
    locate: () => void;
    /** Back to idle; a pending attempt is ignored from then on. */
    reset: () => void;
}

export interface IPricesSearchNearbyResolution {
    state: IPricesSearchNearbyState;
    failureReason: PricesSearchNearbyFailureReason | null;
}

const IDLE: IPricesSearchNearbyResolution = {state: {status: "idle"}, failureReason: null};
const BLOCKED: IPricesSearchNearbyResolution = {state: {status: "blocked"}, failureReason: null};

// GeolocationPositionError.PERMISSION_DENIED - the constant itself is missing where the API is.
const PERMISSION_DENIED_CODE = 1;

// Resolves null wherever the browser cannot say (no Permissions API, or "geolocation" not a name it knows).
const queryGeolocationPermission = async (): Promise<PermissionStatus | null> => {
    try {
        return (await navigator.permissions?.query({name: "geolocation"})) ?? null;
    } catch {
        return null;
    }
};

const PRICES_CITY_NAME_BY_SLUG = new Map<string, string>(PRICES_CITIES.map((city) => [city.slug, city.name]));

// A district of one of the 14 cities first, then the city itself; anywhere else the page does not exist.
export const resolvePricesSearchNearbyRegions = (regions: readonly IRegionByCoordinates[]): IPricesSearchNearbyResolution => {
    const district = regions.find((region) => region.type === RegionType.DISTRICT && region.parent && PRICES_CITY_NAME_BY_SLUG.has(region.parent.slug));

    if (district?.parent) {
        const citySlug = district.parent.slug;
        const target: IPricesSearchNearbyTarget = {
            kind: "district",
            title: district.name,
            href: getDistrictLink(citySlug, district),
            citySlug,
            regionId: district.id,
            // The coordinates lookup uses the region search scenario, so the object is the one the main search
            // stores - but only a region that passes the store's own validation goes there.
            ...(isValidSearchInputRegion(district) ? {region: district} : {})
        };

        return {state: {status: "resolved", target}, failureReason: null};
    }

    const city = regions.find((region) => region.type === RegionType.CITY && PRICES_CITY_NAME_BY_SLUG.has(region.slug));

    if (city) {
        const target: IPricesSearchNearbyTarget = {
            kind: "city",
            title: PRICES_CITY_NAME_BY_SLUG.get(city.slug) ?? city.name,
            href: getCityLink(city.slug),
            citySlug: city.slug,
            regionId: city.id
        };

        return {state: {status: "resolved", target}, failureReason: null};
    }

    const place = regions.find((region) => region.type === RegionType.CITY || region.type === RegionType.TOWN);

    if (place) {
        return {state: {status: "outsideCoverage", placeName: place.name}, failureReason: null};
    }

    // Outside Poland, or only a voivodeship or county came back - there is no place name worth showing.
    return {state: {status: "failed"}, failureReason: "noRegion"};
};

/**
 * "Ceny w mojej okolicy": the device position mapped to a district or city with a prices page. Nothing is
 * asked of the browser until `locate()`; the whole attempt (position and region lookup) gives up after
 * PRICES_SEARCH_GEOLOCATION_TIMEOUT_MS. A site blocked from location is `blocked` - read from the Permissions API
 * up front where the browser has it, otherwise from a denied `locate()` - and goes back to `idle` once the user
 * allows it in the site settings.
 */
export const usePricesSearchNearby = (options: IUsePricesSearchNearbyOptions = {}): IUsePricesSearchNearbyResult => {
    const {locateUser} = useGeolocation();
    const [result, setResult] = useState<IPricesSearchNearbyResolution>(IDLE);
    const isBlockedRef = useRef(false);

    const activeAttemptRef = useRef<number | null>(null);
    const attemptCounterRef = useRef(0);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The latest callbacks, so `locate` can stay stable across renders.
    const locateUserRef = useRef(locateUser);
    const onResolvedRef = useRef(options.onResolved);

    useEffect(() => {
        locateUserRef.current = locateUser;
        onResolvedRef.current = options.onResolved;
    });

    const clearAttempt = useCallback(() => {
        activeAttemptRef.current = null;

        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const settle = useCallback(
        (attempt: number, next: IPricesSearchNearbyResolution) => {
            if (activeAttemptRef.current !== attempt) {
                return;
            }

            clearAttempt();
            setResult(next);

            if (next.state.status === "resolved") {
                onResolvedRef.current?.(next.state.target);
            }
        },
        [clearAttempt]
    );

    const setBlocked = useCallback(
        (isBlocked: boolean) => {
            isBlockedRef.current = isBlocked;

            if (isBlocked) {
                // An attempt in flight has no chance any more.
                clearAttempt();
                setResult(BLOCKED);
            } else {
                setResult((previous) => (previous.state.status === "blocked" ? IDLE : previous));
            }
        },
        [clearAttempt]
    );

    useEffect(() => {
        let isMounted = true;
        let permission: PermissionStatus | null = null;
        const onPermissionChange = () => permission && setBlocked(permission.state === "denied");

        void queryGeolocationPermission().then((status) => {
            if (!isMounted || !status) {
                return;
            }

            permission = status;
            onPermissionChange();
            status.addEventListener("change", onPermissionChange);
        });

        return () => {
            isMounted = false;
            permission?.removeEventListener("change", onPermissionChange);
        };
    }, [setBlocked]);

    const locate = useCallback(() => {
        if (activeAttemptRef.current !== null || isBlockedRef.current) {
            return;
        }

        attemptCounterRef.current += 1;
        const attempt = attemptCounterRef.current;
        activeAttemptRef.current = attempt;

        // useGeolocation silently does nothing without the API, so this case is caught here.
        if (typeof navigator === "undefined" || !navigator.geolocation) {
            settle(attempt, {state: {status: "failed"}, failureReason: "unsupported"});
            return;
        }

        setResult({state: {status: "locating"}, failureReason: null});
        timeoutRef.current = setTimeout(() => settle(attempt, {state: {status: "failed"}, failureReason: "timeout"}), PRICES_SEARCH_GEOLOCATION_TIMEOUT_MS);

        locateUserRef.current(
            (coords) => {
                if (activeAttemptRef.current !== attempt) {
                    return;
                }

                // Two-argument then: an error thrown by `onResolved` must not be reported as a failed lookup.
                void fetchRegionsByCoordinates({}, coords.latitude, coords.longitude).then(
                    (regions) => settle(attempt, resolvePricesSearchNearbyRegions(regions)),
                    () => settle(attempt, {state: {status: "failed"}, failureReason: "lookup"})
                );
            },
            (error) => {
                if (activeAttemptRef.current !== attempt) {
                    return;
                }

                if (error?.code === PERMISSION_DENIED_CODE) {
                    setBlocked(true);
                    return;
                }

                settle(attempt, {state: {status: "failed"}, failureReason: "geolocation"});
            }
        );
    }, [settle, setBlocked]);

    const reset = useCallback(() => {
        clearAttempt();
        setResult(isBlockedRef.current ? BLOCKED : IDLE);
    }, [clearAttempt]);

    useEffect(() => clearAttempt, [clearAttempt]);

    return {state: result.state, failureReason: result.failureReason, locate, reset};
};
